import { toApiError } from '@/lib/http';
import {
  parseKhutbahReading,
  parseLanguageList,
  type KhutbahReading,
} from './types';

// Re-exported so the reader's own modules keep one import for its errors.
export { ApiError } from '@/lib/http';

/**
 * Reading requests.
 *
 * Request functions live here rather than in components, and they return typed
 * data or an actionable error — never a bare `Response` for a component to
 * interpret. Every one takes the `AbortSignal` through to `fetch`, because
 * switching khutbah or language has to *cancel* the outgoing request, not
 * merely ignore its answer: a slower request for the previous khutbah
 * overwriting the current one is the stale-result race the standards call out
 * by name.
 */

export async function fetchKhutbahReading(
  khutbahId: string,
  language: string,
  signal: AbortSignal,
): Promise<KhutbahReading> {
  const response = await fetch(
    `/api/public/khutbah/${encodeURIComponent(khutbahId)}?language=${encodeURIComponent(language)}`,
    { signal, headers: { Accept: 'application/json' } },
  );
  if (!response.ok) {
    throw await toApiError(response);
  }
  return parseKhutbahReading(await response.json());
}

export async function fetchAvailableLanguages(
  khutbahId: string,
  signal: AbortSignal,
): Promise<readonly string[]> {
  const response = await fetch(
    `/api/public/khutbah/${encodeURIComponent(khutbahId)}/languages`,
    { signal, headers: { Accept: 'application/json' } },
  );
  if (!response.ok) {
    throw await toApiError(response);
  }
  return parseLanguageList(await response.json());
}
